<template>
  <div>
    <!-- Contact Hero -->
    <div class="contact-hero">
      <div class="container">
        <div class="section-label fade-up" style="display:inline-flex;margin-bottom:1rem;">{{ $t('contact.heroLabel') }}</div>
        <h1 class="fade-up fade-up-delay-1" v-html="heroTitleHtml"></h1>
        <p class="fade-up fade-up-delay-2">{{ $t('contact.heroSub') }}</p>
      </div>
    </div>

    <!-- Contact Content -->
    <section class="contact-section">
      <div class="contact-grid">

        <!-- Left: Info -->
        <div class="contact-info fade-up">
          <div>
            <h2>{{ $t('contact.infoTitle') }}</h2>
            <p>{{ $t('contact.infoDesc') }}</p>
          </div>

          <div class="contact-methods">
            <div class="contact-method">
              <div class="cm-icon cm-blue">
                <svg viewBox="0 0 24 24">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
              </div>
              <div class="cm-text">
                <div class="label">{{ $t('contact.emailLabel') }}</div>
                <div class="value">{{ $t('contact.emailValue') }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right: Form -->
        <div class="contact-form-card fade-up fade-up-delay-2">
          <h2 style="font-size:1.3rem;font-weight:700;margin-bottom:0.3rem;">{{ $t('contact.formTitle') }}</h2>
          <p style="font-size:0.875rem;color:var(--text-muted);margin-bottom:1.75rem;">{{ $t('contact.formSub') }}</p>

          <form novalidate @submit.prevent="handleSubmit">
            <div class="form-group">
              <label>{{ $t('contact.labelName') }}</label>
              <input
                type="text"
                v-model="form.name"
                :placeholder="$t('contact.placeholderName')"
                :class="{ error: errors.name }"
              />
              <span v-if="errors.name" class="field-error">{{ errors.name }}</span>
            </div>

            <div class="form-group">
              <label>{{ $t('contact.labelEmail') }}</label>
              <input
                type="email"
                v-model="form.email"
                :placeholder="$t('contact.placeholderEmail')"
                :class="{ error: errors.email }"
              />
              <span v-if="errors.email" class="field-error">{{ errors.email }}</span>
            </div>

            <div class="form-group">
              <label>
                {{ $t('contact.labelCompany') }}
                <span style="color:var(--text-light);font-weight:400;">{{ $t('contact.optionalLabel') }}</span>
              </label>
              <input
                type="text"
                v-model="form.company"
                :placeholder="$t('contact.placeholderCompany')"
              />
            </div>

            <div class="form-group">
              <label>{{ $t('contact.labelMessage') }}</label>
              <textarea
                v-model="form.message"
                :placeholder="$t('contact.placeholderMessage')"
                :class="{ error: errors.message }"
              ></textarea>
              <span v-if="errors.message" class="field-error">{{ errors.message }}</span>
            </div>

            <button type="submit" class="form-submit" :disabled="submitting">
              {{ submitting ? $t('contact.submitBtnSending') : $t('contact.submitBtn') }}
            </button>
            <p v-if="sendError" class="send-error">{{ sendError }}</p>
            <p class="form-note">{{ $t('contact.formNote') }}</p>
          </form>
        </div>

      </div>
    </section>

    <!-- Toast -->
    <div class="toast" :class="{ show: showToast }">
      {{ $t('contact.toastMsg') }}
    </div>
  </div>
</template>

<script>
  import { fadeUpMixin } from '../composables/useFadeUp.js'

  export default {
    name: 'ContactPage',
    mixins: [fadeUpMixin],
    data: () => ({
      form: { name: '', email: '', company: '', message: '' },
      errors: { name: '', email: '', message: '' },
      submitting: false,
      showToast: false,
      sendError: '',
    }),
    computed: {
      heroTitleHtml() {
        return this.$t('contact.heroTitle').replace('\n', '<br/>')
      }
    },
    methods: {
      validate() {
        let valid = true
        this.errors.name = ''
        this.errors.email = ''
        this.errors.message = ''

        if (!this.form.name.trim()) {
          this.errors.name = this.$t('contact.validationName')
          valid = false
        }
        if (!this.form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email)) {
          this.errors.email = this.$t('contact.validationEmail')
          valid = false
        }
        if (!this.form.message.trim()) {
          this.errors.message = this.$t('contact.validationMessage')
          valid = false
        }
        return valid
      },
      async handleSubmit() {
        if (!this.validate()) return
        this.submitting = true
        this.sendError = ''

        try {
          const response = await fetch('/contact-form', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: this.form.name,
              email: this.form.email,
              company: this.form.company,
              message: this.form.message,
            }),
          })

          if (!response.ok) {
            this.sendError = response.status === 429 ? this.$t('contact.rateLimitError') : this.$t('contact.sendError')
            return
          }

          this.form.name = ''
          this.form.email = ''
          this.form.company = ''
          this.form.message = ''
          this.showToast = true
          setTimeout(() => { this.showToast = false }, 4000)
        } catch {
          this.sendError = this.$t('contact.sendError')
        } finally {
          this.submitting = false
        }
      }
    }
  }
</script>

<style scoped>
.contact-hero {
  padding-top: 140px;
  padding-bottom: 60px;
  text-align: center;
  background: linear-gradient(180deg, rgba(91,141,184,0.07) 0%, transparent 100%);
}

.contact-hero h1 {
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  margin-bottom: 0.75rem;
}

.contact-hero p {
  font-size: 1.1rem;
  color: var(--text-muted);
  max-width: 460px;
  margin: 0 auto;
}

.contact-section { padding: 60px 2rem 100px; }

.contact-grid {
  display: grid;
  grid-template-columns: 1fr 1.6fr;
  gap: 4rem;
  align-items: start;
  max-width: 1000px;
  margin: 0 auto;
}

.contact-info { display: flex; flex-direction: column; gap: 1.5rem; }

.contact-info h2 { font-size: 1.4rem; font-weight: 700; margin-bottom: 0.25rem; }
.contact-info p { font-size: 0.9rem; color: var(--text-muted); line-height: 1.65; }

.contact-methods { display: flex; flex-direction: column; gap: 1rem; margin-top: 0.5rem; }

.contact-method {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  padding: 1rem 1.1rem;
  background: white;
  border-radius: var(--radius-md);
  border: 1px solid var(--border);
  transition: border-color var(--transition), box-shadow var(--transition);
}

.contact-method:hover { border-color: var(--primary-light); box-shadow: var(--shadow-sm); }

.cm-icon {
  width: 40px; height: 40px;
  border-radius: var(--radius-sm);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}

.cm-icon svg { width: 18px; height: 18px; fill: none; stroke-width: 2; }
.cm-blue { background: rgba(91,141,184,0.12); }
.cm-blue svg { stroke: var(--primary); }
.cm-green { background: rgba(123,175,150,0.12); }
.cm-green svg { stroke: var(--secondary); }

.cm-text .label { font-size: 0.72rem; color: var(--text-muted); font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; }
.cm-text .value { font-size: 0.9rem; font-weight: 600; color: var(--text); margin-top: 2px; }

/* Contact Form */
.contact-form-card {
  background: white;
  border-radius: var(--radius-xl);
  border: 1px solid var(--border);
  padding: 2.5rem;
  box-shadow: var(--shadow-md);
}

.form-group { margin-bottom: 1.25rem; }

.form-group label {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text);
  margin-bottom: 0.4rem;
}

.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1.5px solid var(--border);
  border-radius: var(--radius-sm);
  font-size: 0.9rem;
  color: var(--text);
  background: var(--bg);
  font-family: inherit;
  transition: border-color var(--transition), box-shadow var(--transition);
  outline: none;
  -webkit-appearance: none;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(91, 141, 184, 0.15);
  background: white;
}

.form-group textarea { min-height: 130px; resize: vertical; }

.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }

.form-submit {
  width: 100%;
  padding: 0.9rem;
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
  color: white;
  font-size: 1rem;
  font-weight: 700;
  border-radius: var(--radius-md);
  border: none;
  cursor: pointer;
  transition: opacity var(--transition), transform var(--transition);
  box-shadow: 0 4px 14px rgba(91, 141, 184, 0.4);
}

.form-submit:hover { opacity: 0.92; transform: translateY(-1px); }

.form-note { text-align: center; font-size: 0.78rem; color: var(--text-light); margin-top: 0.75rem; }

.send-error {
  text-align: center;
  font-size: 0.85rem;
  color: #e53e3e;
  margin-top: 0.75rem;
  padding: 0.6rem 1rem;
  background: rgba(229, 62, 62, 0.06);
  border-radius: var(--radius-sm);
  border: 1px solid rgba(229, 62, 62, 0.2);
}

/* ─── Success toast ─────────────────────────────────────────────────── */
.toast {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  background: var(--secondary);
  color: white;
  padding: 1rem 1.5rem;
  border-radius: var(--radius-md);
  font-size: 0.9rem;
  font-weight: 600;
  box-shadow: var(--shadow-lg);
  transform: translateY(100px);
  opacity: 0;
  transition: all 0.4s ease;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 8px;
}

.toast.show { transform: translateY(0); opacity: 1; }

.field-error {
  display: block;
  font-size: 0.78rem;
  color: #e53e3e;
  margin-top: 0.25rem;
}

.form-group input.error,
.form-group textarea.error {
  border-color: #e53e3e;
}

@media (max-width: 900px) {
  .contact-grid { grid-template-columns: 1fr; gap: 2rem; }
}

@media (max-width: 700px) {
  .form-row { grid-template-columns: 1fr; }
}
</style>
